import { alpha, Box, Button, Stack, Tab, Tabs } from '@mui/material';
import styles from 'components/common/page-menu/PageMenu.styles';
import React, { useState } from 'react';
import ReactSelect from 'components/common/react-select/ReactSelect';
import { FormComponentValue } from 'components/common/form/layout/control';
import useBundleTranslation from 'i18n';
import IconMi from 'components/common/icon/IconMi';
import FormComponent from 'components/common/form/Form';
import GenerateAlertEventsButton from './GenerateAlertEventsButton';

export interface InfobarProps {
    rulesSelectData: FormComponentValue[];
    selectedRuleId: string;
    selectedRuleName: string;
    onRuleSelect: (value: string) => void;
    onBack: () => void;
    onSave: () => Promise<boolean>;
    filterDirty: boolean;
    isPermitted4Edit: boolean;
    elementId: number;
    onRuleAdded: (newRuleId: number) => void;
    onRulesListReload: () => void;
}

function Infobar({
    rulesSelectData,
    selectedRuleId,
    selectedRuleName,
    onRuleSelect,
    onBack,
    onSave,
    filterDirty,
    isPermitted4Edit,
    elementId,
    onRuleAdded,
    onRulesListReload,
}: InfobarProps) {
    const { t } = useBundleTranslation(['app/report/report-alert-rule']);

    const [showEditRulePopup, setShowEditRulePopup] = useState(false);
    const [showAddRulePopup, setShowAddRulePopup] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const handleSave = async () => {
        setIsSaving(true);
        const result = await onSave();
        setIsSaving(false);
        return result;
    };

    return (
        <Stack
            direction={'row'}
            justifyContent="space-between"
            alignItems="center"
            sx={{
                px: 1,
                borderBottom: '1px solid',
                borderColor: (theme) => alpha(theme.palette.text.primary, 0.24),
                flexShrink: 0,
            }}
        >
            <Stack direction={'row'} alignItems="center" spacing={2}>
                <Tabs
                    value={false}
                    onChange={() => onBack()}
                    sx={{ ...styles.tabs, mb: 0, flexShrink: 0, flexGrow: 0 }}
                >
                    <Tab
                        icon={<IconMi icon={'chevron-left'} sx={styles.tabItem} />}
                        iconPosition="start"
                        label={<Box sx={styles.labelHolder}>{t('back')}</Box>}
                    />
                </Tabs>
                <Box
                    sx={{
                        width: '1px',
                        height: '16px',
                        backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.08),
                        flexShrink: 0,
                    }}
                />

                <Box sx={{ minWidth: 250 }}>
                    <ReactSelect data={rulesSelectData} value={selectedRuleId} update={onRuleSelect} />
                </Box>

                {isPermitted4Edit && (
                    <Button
                        variant="outlined"
                        color="neutral"
                        startIcon={<IconMi icon="pencil" />}
                        onClick={() => setShowEditRulePopup(true)}
                    >
                        {t('rename')}
                    </Button>
                )}
            </Stack>

            <Stack direction={'row'} spacing={1} alignItems="center">
                {isPermitted4Edit && (
                    <>
                        <Button
                            variant="outlined"
                            color="success"
                            startIcon={<IconMi icon="new" />}
                            onClick={() => setShowAddRulePopup(true)}
                        >
                            {t('add_alert_rule')}
                        </Button>

                        <GenerateAlertEventsButton
                            disabled={isSaving}
                            elementId={elementId}
                            beforeGenerate={async () => {
                                let formSaved = true;
                                if (filterDirty) {
                                    formSaved = await handleSave();
                                }
                                return Promise.resolve(formSaved);
                            }}
                        >
                            <Button
                                variant="contained"
                                color="success"
                                disabled={isSaving}
                                startIcon={<IconMi icon="refresh-cw" />}
                            >
                                {filterDirty ? t('save') + ' & ' + t('generate') : t('generate')}
                            </Button>
                        </GenerateAlertEventsButton>

                        <Button
                            variant="contained"
                            color="success"
                            disabled={!filterDirty || isSaving}
                            onClick={handleSave}
                            startIcon={<IconMi icon="save" />}
                        >
                            {t('save')}
                        </Button>
                    </>
                )}
            </Stack>

            {showEditRulePopup && (
                <FormComponent
                    isPopup
                    popupType="new"
                    pk="reportAlertRuleId"
                    uid={`report-alert-rule-form-edit-${elementId}-${selectedRuleId}`}
                    settingsUrl={`/data/editor/element/report/${elementId}/alert-rule/${selectedRuleId}/form`}
                    onHide={() => setShowEditRulePopup(false)}
                    popupSettings={{ title: t('edit_rule_popup.title'), maxWidth: 'popupSm' }}
                    afterSave={() => {
                        setShowEditRulePopup(false);
                        onRulesListReload();
                    }}
                />
            )}

            {showAddRulePopup && (
                <FormComponent
                    isPopup
                    popupType="new"
                    pk="reportAlertRuleId"
                    uid={`report-alert-rule-form-${elementId}`}
                    popupSettings={{ maxWidth: 'popupSm' }}
                    settingsUrl={`/data/editor/element/report/${elementId}/alert-rule/{reportAlertRuleId}/form`}
                    onHide={() => setShowAddRulePopup(false)}
                    afterSave={(response: any) => {
                        setShowAddRulePopup(false);
                        if (response?.data?.id) {
                            onRuleAdded(response.data.id);
                        } else {
                            onRulesListReload();
                        }
                    }}
                />
            )}
        </Stack>
    );
}

export default Infobar;
